import { Fragment, useId, useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "../Input/Input";
import { Select } from "../Select/Select";
import styles from "./SchemaEditor.module.css";

export type SchemaFieldType = "int" | "string" | "bool";

export type SchemaField = {
  key: string;
  type: SchemaFieldType;
};

interface Props {
  value: SchemaField[];
  onChange: (next: SchemaField[]) => void;
  /** Optional regex for keys. Default `^[a-z_][a-z0-9_]*$`. */
  keyPattern?: RegExp;
  label?: string;
  helperText?: string;
  disabled?: boolean;
}

const DEFAULT_KEY_PATTERN = /^[a-z_][a-z0-9_]*$/;

const TYPE_OPTIONS = [
  { value: "int", label: "int" },
  { value: "string", label: "string" },
  { value: "bool", label: "bool" },
];

/**
 * SchemaEditor — row-based editor for { key, type } pairs (mockup
 * project-config.html L562-L578 / L686-L701). Composes Input + Select.
 */
export function SchemaEditor({
  value,
  onChange,
  keyPattern = DEFAULT_KEY_PATTERN,
  label,
  helperText,
  disabled,
}: Props) {
  const reactId = useId();
  const labelId = label ? `${reactId}-label` : undefined;

  // Track which keys collide (case-sensitive comparison since the pattern
  // restricts keys to lowercase letters/digits/underscore anyway).
  const dupSet = useMemo(() => {
    const seen = new Map<string, number>();
    const dups = new Set<string>();
    value.forEach((f) => {
      if (!f.key) return;
      const n = (seen.get(f.key) ?? 0) + 1;
      seen.set(f.key, n);
      if (n > 1) dups.add(f.key);
    });
    return dups;
  }, [value]);

  function updateRow(idx: number, next: Partial<SchemaField>) {
    const copy = value.map((row, i) => (i === idx ? { ...row, ...next } : row));
    onChange(copy);
  }

  function removeRow(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  function addRow() {
    onChange([...value, { key: "", type: "int" }]);
  }

  return (
    <div className={styles.wrap}>
      {label && (
        <span id={labelId} className={styles.label}>
          {label}
        </span>
      )}
      <div
        className={styles.editor}
        role="group"
        aria-labelledby={labelId}
      >
        {value.map((row, idx) => {
          const duplicate = !!row.key && dupSet.has(row.key);
          const patternInvalid = !!row.key && !keyPattern.test(row.key);
          const errMsg = duplicate
            ? "Duplicate key"
            : patternInvalid
              ? "Use lowercase letters, digits, underscore"
              : undefined;
          const showError = !!errMsg;
          return (
            <Fragment key={idx}>
              <div className={styles.row}>
                <Input
                  aria-label={`Field ${idx + 1} key`}
                  value={row.key}
                  onChange={(e) => updateRow(idx, { key: e.target.value })}
                  placeholder="field_name"
                  disabled={disabled}
                  error={showError ? errMsg : undefined}
                />
                <Select
                  className={styles.typeSelect}
                  value={row.type}
                  onChange={(v) =>
                    updateRow(idx, { type: v as SchemaFieldType })
                  }
                  options={TYPE_OPTIONS}
                  disabled={disabled}
                />
                <button
                  type="button"
                  className={styles.delete}
                  aria-label={`Delete field ${idx + 1}`}
                  disabled={disabled}
                  onClick={() => removeRow(idx)}
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </div>
            </Fragment>
          );
        })}
        <button
          type="button"
          className={styles.addBtn}
          onClick={addRow}
          disabled={disabled}
        >
          <Plus size={12} aria-hidden />
          Add field
        </button>
      </div>
      {helperText && <div className={styles.helper}>{helperText}</div>}
    </div>
  );
}
